import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const IMAGE_TYPES = [
  'Primary',           // default - used everywhere
  'Schedule',          // FF&E schedule exports
  'Proposal',          // client proposals
  'Purchase Order',    // PO documentation
  'Presentation',      // client presentations
  'Detail / close-up', // finish, texture, material
  'Installed',         // on-site installed photo
  'Dimension drawing', // spec diagram
  'Receiving Photo',   // warehouse receiving photo
];

// Which type to use for each context (falls back to Primary)
const TYPE_FOR_CONTEXT = {
  schedule:  ['Schedule',  'Primary'],
  proposal:  ['Proposal',  'Primary'],
  po:        ['Purchase Order', 'Primary'],
  presentation: ['Presentation', 'Primary'],
  receiving: ['Receiving Photo', 'Primary'],
};

// Helper: get the best image for a given context

const MAX_SIZE = 20 * 1024 * 1024;

function fmtSize(b) {
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
  return (b / (1024 * 1024)).toFixed(1) + ' MB';
}

// Single image upload to Supabase Storage
export async function uploadImage(file, itemId) {
  const ext = file.name.split('.').pop().toLowerCase();
  const safeId = (itemId || 'unknown').toString().replace(/[^a-zA-Z0-9-_]/g, '-');
  const path = `${safeId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  console.log('[ImageUpload] uploading to path:', path);
  const { data: upData, error } = await supabase.storage
    .from('item-images')
    .upload(path, file, { upsert: true, contentType: file.type || 'image/jpeg' });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from('item-images').getPublicUrl(path);
  console.log('[ImageUpload] public URL:', data?.publicUrl);
  if (!data?.publicUrl) throw new Error('Upload succeeded but could not get public URL. Check bucket is set to public in Supabase.');
  return { url: data.publicUrl, path };
}

export default function ItemImageGallery({ images = [], onChange, heroIndex = 0, onHeroChange, itemId: propItemId }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null); // index of enlarged image
  const [editingIdx, setEditingIdx] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editType, setEditType] = useState('');
  const fileRef = useRef(null);

  // itemId derived from first image path or passed via prop
  // We use a stable prefix approach - caller passes itemId
  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).slice(0, 5); // max 5 at a time
    for (const f of arr) {
      if (f.size > MAX_SIZE) { setError(`${f.name} is over 20MB — skipped.`); continue; }
    }
    const valid = arr.filter(f => f.size <= MAX_SIZE);
    if (!valid.length) return;

    setUploading(true); setError('');
    const newImages = [...images];

    for (let i = 0; i < valid.length; i++) {
      setUploadProgress(`Uploading ${i + 1} of ${valid.length}...`);
      try {
        // Get itemId from existing images or generate
        const itemId = propItemId || images[0]?.path?.split('/')[0] || `item-${Date.now()}`;
        const { url, path } = await uploadImage(valid[i], itemId);
        newImages.push({
          url,
          path,
          label: valid[i].name.replace(/\.[^.]+$/, ''),
          type: 'Primary',
          size: valid[i].size,
          addedAt: new Date().toISOString(),
        });
      } catch (e) {
        setError('Upload failed: ' + e.message);
      }
    }

    if(typeof onChange==='function') onChange(newImages);
    setUploading(false);
    setUploadProgress('');
  }

  function handleDrop(e) {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }

  function removeImage(idx) {
    if (!window.confirm('Remove this image?')) return;
    const updated = images.filter((_, i) => i !== idx);
    if(typeof onChange==='function') onChange(updated);
    if (heroIndex >= updated.length && onHeroChange) onHeroChange(0);
    if (lightbox === idx) setLightbox(null);
  }

  function setHero(idx) {
    if (onHeroChange) onHeroChange(idx);
  }

  function startEdit(idx) {
    setEditingIdx(idx);
    setEditLabel(images[idx].label || '');
    setEditType(images[idx].type || 'Full item view');
  }

  function saveEdit(idx) {
    const updated = images.map((img, i) => i === idx ? { ...img, label: editLabel, type: editType } : img);
    if(typeof onChange==='function') onChange(updated);
    setEditingIdx(null);
  }

  function moveImage(idx, dir) {
    const updated = [...images];
    const swap = idx + dir;
    if (swap < 0 || swap >= updated.length) return;
    [updated[idx], updated[swap]] = [updated[swap], updated[idx]];
    if(typeof onChange==='function') onChange(updated);
    if (heroIndex === idx && onHeroChange) onHeroChange(swap);
    else if (heroIndex === swap && onHeroChange) onHeroChange(idx);
  }

  const inp = {
    padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5,
    fontSize: 12, fontFamily: 'inherit', outline: 'none', background: '#fff',
  };

  return (
    <div>
      {/* Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'zoom-out',
        }}>
          <img src={images[lightbox].url} alt={images[lightbox].label}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 4 }}
            onClick={e => e.stopPropagation()} />
          <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            color: '#fff', fontSize: 13, background: 'rgba(0,0,0,0.6)', padding: '6px 16px', borderRadius: 20 }}>
            {images[lightbox].label || images[lightbox].type} · Click anywhere to close
          </div>
          {images.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }}
                style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                  fontSize: 24, cursor: 'pointer', borderRadius: '50%', width: 44, height: 44 }}>‹</button>
              <button onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }}
                style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                  fontSize: 24, cursor: 'pointer', borderRadius: '50%', width: 44, height: 44 }}>›</button>
            </>
          )}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', letterSpacing: 0.3, textTransform: 'uppercase' }}>
          Images ({images.length})
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ padding: '4px 12px', background: '#1a1a1a', color: '#fff', border: 'none',
            borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1 }}>
          {uploading ? uploadProgress || 'Uploading...' : '+ Add Images'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple
          onChange={e => handleFiles(e.target.files)} style={{ display: 'none' }} />
      </div>

      {error && (
        <div style={{ fontSize: 11, color: '#c0392b', marginBottom: 8, padding: '6px 10px',
          background: '#ffebee', borderRadius: 5 }}>{error}</div>
      )}

      {/* Drop zone when empty */}
      {images.length === 0 && (
        <div onDragOver={e => e.preventDefault()} onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          style={{ border: '2px dashed #ddd', borderRadius: 8, padding: '32px 16px',
            textAlign: 'center', cursor: 'pointer', background: '#fafafa',
            transition: 'border-color .15s' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📷</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4 }}>
            Drop images here or click to upload
          </div>
          <div style={{ fontSize: 11, color: '#aaa' }}>
            Up to 20MB each · JPG, PNG, HEIC supported · Multiple files at once
          </div>
        </div>
      )}

      {/* Gallery grid */}
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {images.map((img, idx) => (
            <div key={idx} style={{
              border: heroIndex === idx ? '2px solid #1a1a1a' : '1px solid #e0e0e0',
              borderRadius: 8, overflow: 'hidden', background: '#fff',
              position: 'relative',
            }}>
              {/* Procurement image checkmark */}
              <div onClick={(e) => { e.stopPropagation(); setHero(idx); }}
                title={heroIndex === idx ? "Used in procurement docs" : "Click to use in procurement docs"}
                style={{ position: 'absolute', top: 6, left: 6, zIndex: 2, cursor: 'pointer',
                  width: 22, height: 22, borderRadius: '50%',
                  background: heroIndex === idx ? '#2e7d32' : 'rgba(255,255,255,0.85)',
                  border: heroIndex === idx ? '2px solid #2e7d32' : '2px solid #ccc',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}>
                {heroIndex === idx ? '✓' : ''}
              </div>

              {/* Image */}
              {editingIdx === idx ? (
                <div style={{ padding: 10, background: '#fffde7' }}>
                  <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                    placeholder="Image label" style={{ ...inp, width: '100%', marginBottom: 6 }} />
                  <select value={editType} onChange={e => setEditType(e.target.value)}
                    style={{ ...inp, width: '100%', marginBottom: 8 }}>
                    {IMAGE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => saveEdit(idx)} style={{
                      flex: 1, padding: '4px 0', background: '#2e7d32', color: '#fff',
                      border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => setEditingIdx(null)} style={{
                      flex: 1, padding: '4px 0', background: '#f5f5f5', border: '1px solid #ddd',
                      borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#666' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div onClick={() => setLightbox(idx)}
                    style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', cursor: 'zoom-in', background: '#f5f5f5', position: 'relative' }}>
                    <img src={img.url} alt={img.label || ''}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }} />
                    <div style={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center',
                      justifyContent: 'center', flexDirection: 'column', background: '#fff8e1',
                      fontSize: 10, color: '#e65100', padding: 8, textAlign: 'center' }}>
                      <div style={{ marginBottom: 4 }}>⚠ Image not loading</div>
                      <div style={{ fontSize: 9, color: '#aaa', wordBreak: 'break-all' }}>{img.url?.slice(0, 60)}...</div>
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ padding: '6px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#333',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      marginBottom: 1 }}>
                      {img.label || img.type || 'Image'}
                    </div>
                    <div style={{ fontSize: 9, color: '#aaa' }}>
                      {img.type || 'Primary'}{img.size ? ' · ' + fmtSize(img.size) : ''}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ padding: '0 6px 6px', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {heroIndex === idx ? (
                      <span style={{ fontSize: 9, color: '#2e7d32', fontWeight: 700, padding: '2px 4px' }}>
                        ✓ Procurement image
                      </span>
                    ) : (
                      <button onClick={() => setHero(idx)} style={{
                        padding: '2px 6px', background: '#f0f4ff', border: '1px solid #c7d7f8',
                        borderRadius: 3, fontSize: 9, fontWeight: 600, cursor: 'pointer', color: '#1a56db' }}>
                        Use for procurement
                      </button>
                    )}
                    <button onClick={() => startEdit(idx)} style={{
                      padding: '2px 6px', background: '#e8f0fe', border: 'none',
                      borderRadius: 3, fontSize: 9, fontWeight: 600, cursor: 'pointer', color: '#1a56db' }}>
                      Edit
                    </button>
                    {idx > 0 && (
                      <button onClick={() => moveImage(idx, -1)} style={{
                        padding: '2px 5px', background: '#f5f5f5', border: '1px solid #ddd',
                        borderRadius: 3, fontSize: 9, cursor: 'pointer', color: '#555' }}>←</button>
                    )}
                    {idx < images.length - 1 && (
                      <button onClick={() => moveImage(idx, 1)} style={{
                        padding: '2px 5px', background: '#f5f5f5', border: '1px solid #ddd',
                        borderRadius: 3, fontSize: 9, cursor: 'pointer', color: '#555' }}>→</button>
                    )}
                    <button onClick={() => removeImage(idx)} style={{
                      padding: '2px 6px', background: '#ffebee', border: 'none',
                      borderRadius: 3, fontSize: 9, fontWeight: 600, cursor: 'pointer', color: '#c62828',
                      marginLeft: 'auto' }}>✕</button>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Add more tile */}
          {images.length < 10 && (
            <div onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()} onDrop={handleDrop}
              style={{ border: '2px dashed #e0e0e0', borderRadius: 8, aspectRatio: '4/3',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', background: '#fafafa', fontSize: 11, color: '#bbb', gap: 4 }}>
              <span style={{ fontSize: 20 }}>+</span>
              <span>Add image</span>
            </div>
          )}
        </div>
      )}

      {images.length > 0 && (
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>
          Click any image to enlarge · Set hero for schedules and spec sheets · Drag ← → to reorder
        </div>
      )}
    </div>
  );
}
